import React, { useState, useEffect } from 'react';
import { renderIcon } from '../../utils/iconRenderer';
import { FaEye, FaEdit, FaTrash, FaSearch, FaHeart, FaComment, FaGlobe } from 'react-icons/fa';
import { posts, comments, groupMessages, privateMessages } from '../../data/mockData';
import { collection, onSnapshot, query, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, storage, functions } from '../../firebase';
import ConfirmModal from '../ConfirmModal';

const UsersModule = ({ selectedCountry }) => {
    const [users, setUsers] = useState([]);
    const [userActivityFilter, setUserActivityFilter] = useState('all');
    const [viewingUser, setViewingUser] = useState(null);

    const getInitials = (name) => {
        if (!name || name === 'Anonymous') return '??';
        const parts = name.split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
        return name.slice(0, 2).toUpperCase();
    };
    const [editingUser, setEditingUser] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(null); // UID of user being deleted
    const [selectedImageFile, setSelectedImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [confirmConfig, setConfirmConfig] = useState({ 
        isOpen: false, 
        title: '', 
        message: '', 
        type: 'danger', 
        onConfirm: null, 
        singleButton: false 
    });

    useEffect(() => {
        const q = query(collection(db, 'users'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const usersData = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                usersData.push({
                    id: doc.id,
                    ...data, // Spread all raw data
                    name: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Anonymous',
                    email: data.email || 'No Email',
                    country: data.country || 'Unknown',
                    role: data.role || 'User',
                    status: data.status === 2 ? 'Active' : (data.status === 0 ? 'Disabled' : 'Basic'),
                    avatar: data.photoUrl || 'male',
                    joinedDate: data.createdAt ? new Date(data.createdAt.seconds * 1000).toLocaleDateString() : 'N/A',
                    posts: 0,
                    gmail: data.gmail || 'N/A',
                    pseudonym: data.firstNamePseudonym || 'No Pseudo',
                    alternateId: data.countryPseudonym || 'No Country Pseudo'
                });
            });
            setUsers(usersData.sort((a, b) => {
                const tA = a.createdAt?.seconds ?? 0;
                const tB = b.createdAt?.seconds ?? 0;
                return tB - tA; // newest first
            }));
        }, (error) => {
            console.error("Error fetching users: ", error);
        });

        return () => unsubscribe();
    }, []);

    const handleDeleteUser = async (user) => {
        try {
            setIsDeleting(user.id);
            // Call the Cloud Function
            const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
            
            const result = await deleteUserAccount({ 
                uid: user.id, 
                email: user.email 
            });

            if (result.data.success) {
                if (viewingUser && viewingUser.id === user.id) {
                    setViewingUser(null);
                }
                setConfirmConfig({
                    isOpen: true,
                    title: "User Deleted",
                    message: `User ${user.name} has been removed successfully.`,
                    type: "success",
                    singleButton: true
                });
            }
        } catch (error) {
            console.error("Error deleting user via Cloud Function: ", error);
            setConfirmConfig({
                isOpen: true,
                title: "Deletion Failed",
                message: "Could not delete user: " + error.message,
                type: "danger",
                singleButton: true
            });
        } finally {
            setIsDeleting(null);
        }
    };

     const handleUpdateUser = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            let photoUrl = editingUser.photoUrl;

            // Handle Image Upload if a new file is selected
            if (selectedImageFile) {
                const storageRef = ref(storage, `users/${editingUser.id}_${Date.now()}`);
                const snapshot = await uploadBytes(storageRef, selectedImageFile);
                photoUrl = await getDownloadURL(snapshot.ref);
            }

            const { id, ...updatedData } = editingUser;
            updatedData.photoUrl = photoUrl; // Update with new URL or keep old one

            // Remove helper fields that shouldn't be in Firestore
            delete updatedData.name;
            delete updatedData.joinedDate;
            delete updatedData.avatar;
            delete updatedData.posts;
            delete updatedData.pseudonym;
            delete updatedData.alternateId;
            delete updatedData.statusText; // If any

            // If status was mapped to string, convert back to number for DB
            if (typeof updatedData.status === 'string') {
                const statusMap = { 'Active': 2, 'Basic': 1, 'Disabled': 0 };
                updatedData.status = statusMap[updatedData.status] ?? 1;
            }

            await updateDoc(doc(db, 'users', id), updatedData);

            setConfirmConfig({
                isOpen: true,
                title: "Update Successful",
                message: "User information has been updated securely.",
                type: "success",
                singleButton: true
            });
            setEditingUser(null);
            setSelectedImageFile(null);
            setImagePreview(null);
        } catch (error) {
            console.error("Error updating user:", error);
            setConfirmConfig({
                isOpen: true,
                title: "Update Failed",
                message: "An error occurred while updating: " + error.message,
                type: "danger",
                singleButton: true
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Filter users by country and search query
    const filteredUsers = users.filter(user => {
        const matchesCountry = selectedCountry === 'All' || user.country === selectedCountry;
        const query = searchQuery.toLowerCase();
        const matchesSearch =
            user.name.toLowerCase().includes(query) ||
            user.pseudonym.toLowerCase().includes(query) ||
            user.alternateId.toLowerCase().includes(query) ||
            user.email.toLowerCase().includes(query);

        return matchesCountry && matchesSearch;
    });

    const [visibleCount, setVisibleCount] = useState(20);
    const paginatedUsers = filteredUsers.slice(0, visibleCount);

    if (viewingUser) {
        return (
            <div className="content-section fade-in">
                <div className="content-header-minimal">
                    <button className="back-btn" onClick={() => setViewingUser(null)}>← Back to Users</button>
                    <div className="header-actions">
                        <button 
                            className="btn-action delete-profile-btn" 
                            title="Delete User Account" 
                            onClick={() => setConfirmConfig({
                                isOpen: true,
                                title: "Permanently Delete Account?",
                                message: `Are you sure you want to delete ${viewingUser.name}'s account? This action will permanently remove all data and cannot be undone.`,
                                type: "danger",
                                onConfirm: () => handleDeleteUser(viewingUser)
                            })}
                            disabled={isDeleting === viewingUser.id}
                        >
                            {isDeleting === viewingUser.id ? (
                                <div className="spinner-mini" />
                            ) : (
                                <><FaTrash size={14} style={{ marginRight: '8px' }} /> Delete Account</>
                            )}
                        </button>
                    </div>
                </div>

                <div className="user-profile-header-new">
                    <div className="profile-avatar-large">
                        {viewingUser.avatar && viewingUser.avatar.startsWith('http') ? (
                            <img src={viewingUser.avatar} alt="Avatar" />
                        ) : (
                            getInitials(viewingUser.name)
                        )}
                    </div>
                    <div className="profile-info-main">
                        <h2>
                            {viewingUser.name} 
                            <span className="role-tag">{viewingUser.role}</span>
                        </h2>
                        <p>
                            <span className="info-chip">{viewingUser.email}</span>
                            <span className="info-chip-divider">•</span>
                            <span className="info-chip">{viewingUser.country}</span>
                            <span className="info-chip-divider">•</span>
                            <span className="info-chip">Joined {viewingUser.joinedDate}</span>
                        </p>
                        <div className="pseudonym-tags">
                            <span className="pseudo-tag">@{viewingUser.pseudonym}</span>
                            <span className="pseudo-tag">@{viewingUser.alternateId}</span>
                        </div>
                    </div>
                </div>

                <div className="user-details-grid">
                    <div className="details-card">
                        <h3>Personal Information</h3>
                        <div className="details-info-grid">
                            <div className="info-item">
                                <label>First Name</label>
                                <span>{viewingUser.firstName || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Last Name</label>
                                <span>{viewingUser.lastName || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Email Address</label>
                                <span>{viewingUser.email}</span>
                            </div>

                             <div className="info-item">
                                <label>Country</label>
                                <span>{viewingUser.country}</span>
                            </div>
                            <div className="info-item">
                                <label>Phone Number</label>
                                <span>{viewingUser.phoneNumber || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                    <div className="details-card">
                        <h3>Account & Pseudonyms</h3>
                        <div className="details-info-grid">
                            <div className="info-item">
                                <label>Role</label>
                                <span className={`role-badge ${viewingUser.role.toLowerCase()}`}>{viewingUser.role}</span>
                            </div>
                            <div className="info-item">
                                <label>Status</label>
                                <span className={`status-pill ${viewingUser.status.toLowerCase()}`}>{viewingUser.status}</span>
                            </div>
                            <div className="info-item">
                                <label>First Name Pseudonym</label>
                                <span>{viewingUser.firstNamePseudonym || 'N/A'}</span>
                            </div>
                            <div className="info-item">
                                <label>Country Pseudonym</label>
                                <span>{viewingUser.countryPseudonym || 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="details-card uid-card" style={{ marginTop: '24px', background: 'var(--bg-secondary)', border: '2px dashed var(--border-color)' }}>
                    <h3 style={{ borderBottomColor: 'var(--border-color)' }}>System Information (User ID)</h3>
                    <div className="id-container" style={{ padding: '20px', background: 'var(--bg-card)', borderRadius: '12px', border: '1fr solid var(--border-light)' }}>
                        <code style={{ fontSize: '18px', color: 'var(--primary-blue)', fontWeight: 700, letterSpacing: '1px' }}>
                            {viewingUser.id}
                        </code>
                        <p style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                            This is the unique identifier for the user in the database.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="content-section fade-in">
            <div className="content-header">
                <div>
                    <h2 className="content-title">User Management</h2>
                    <p style={{ margin: 0, color: 'var(--text-tertiary)', fontSize: '14px' }}>
                        Showing {filteredUsers.length} users {selectedCountry !== 'All' ? `in ${selectedCountry}` : 'globally'}
                    </p>
                </div>
                <div className="header-actions">
                    <div className="search-bar-mini">
                        <FaSearch className="search-icon-mini" />
                        <input
                            type="text"
                            placeholder="Search Name, Pseudos..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            <div className="table-container">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Location</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>User ID</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedUsers.map(user => (
                            <tr key={user.id}>
                                <td>
                                    <div className="user-cell">
                                         <div className="user-avatar">
                                            {user.avatar.startsWith('http') ? (
                                                <img src={user.avatar} alt="Avatar" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                            ) : (
                                                renderIcon(user.avatar, 18)
                                            )}
                                        </div>
                                        <div className="user-info">
                                            <span className="user-name">{user.name}</span>
                                            <span className="user-email" style={{ fontSize: '11px', display: 'flex', gap: '8px', color: 'var(--primary-blue)' }}>
                                                <span>{user.pseudonym}</span>
                                                <span>{user.alternateId}</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                                        <FaGlobe size={12} color="var(--text-tertiary)" />
                                        {user.country}
                                    </div>
                                </td>
                                <td>
                                    <span className={`role-badge ${user.role.toLowerCase()}`}>
                                        {user.role}
                                    </span>
                                </td>
                                <td>
                                    <span className={`status-pill ${user.status.toLowerCase()}`}>
                                        {user.status}
                                    </span>
                                </td>
                                <td>
                                    <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'monospace' }}>
                                        {user.id}
                                    </div>
                                </td>
                                <td>
                                    <div className="action-buttons">
                                        <button
                                            className="btn-action view"
                                            title="View Profile"
                                            onClick={() => setViewingUser(user)}
                                        >
                                            <FaEye />
                                        </button>
                                        <button className="btn-action edit" title="Edit Profile" onClick={() => setEditingUser(user)}><FaEdit /></button>
                                        <button 
                                            className="btn-action delete" 
                                            title="Delete User" 
                                            onClick={() => setConfirmConfig({
                                                isOpen: true,
                                                title: "Permanently Delete Account?",
                                                message: `Are you sure you want to delete ${user.name}'s account? This action will permanently remove all data and cannot be undone.`,
                                                type: "danger",
                                                onConfirm: () => handleDeleteUser(user)
                                            })}
                                            disabled={isDeleting === user.id}
                                        >
                                            {isDeleting === user.id ? '...' : <FaTrash />}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {visibleCount < filteredUsers.length && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <button className="btn-add" onClick={() => setVisibleCount(visibleCount + 20)}>
                            Load More Users
                        </button>
                    </div>
                )}
                {filteredUsers.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-tertiary)' }}>
                        No users found matching your criteria.
                    </div>
                )}
            </div>

            {/* Edit User Modal */}
            {editingUser && (
                <div className="modal-overlay">
                    <div className="modal-content-custom">
                        <div className="modal-header-custom">
                            <h2>Edit User: {editingUser.name}</h2>
                            <button className="close-btn" onClick={() => setEditingUser(null)}>&times;</button>
                        </div>
                        <form onSubmit={handleUpdateUser} className="edit-form">
                            <div className="edit-form-grid">
                                <div className="form-group-custom">
                                    <label>First Name</label>
                                    <input
                                        type="text"
                                        value={editingUser.firstName || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, firstName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Last Name</label>
                                    <input
                                        type="text"
                                        value={editingUser.lastName || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, lastName: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Phone Number</label>
                                    <input
                                        type="text"
                                        value={editingUser.phoneNumber || editingUser.phone || editingUser.mobile || ''}
                                        onChange={(e) => setEditingUser({ ...editingUser, phoneNumber: e.target.value, phone: e.target.value })}
                                        placeholder="Enter phone number"
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Profile Image</label>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                        <div className="preview-avatar" style={{ width: '50px', height: '50px', flexShrink: 0, borderRadius: '50%', overflow: 'hidden', border: '1px solid var(--border-light)' }}>
                                            {imagePreview || editingUser.photoUrl ? (
                                                <img src={imagePreview || editingUser.photoUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', background: 'var(--bg-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', color: 'var(--text-tertiary)' }}>No Image</div>
                                            )}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            style={{ fontSize: '12px' }}
                                        />
                                    </div>
                                </div>
                                <div className="form-group-custom">
                                    <label>Email (Read-only)</label>
                                    <input
                                        type="email"
                                        value={editingUser.email || ''}
                                        disabled
                                        style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Country (Read-only)</label>
                                    <input
                                        type="text"
                                        value={editingUser.country || ''}
                                        disabled
                                        style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>First Name Pseudonym (Read-only)</label>
                                    <input
                                        type="text"
                                        value={editingUser.firstNamePseudonym || ''}
                                        disabled
                                        style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                    />
                                </div>
                                <div className="form-group-custom">
                                    <label>Country Pseudonym (Read-only)</label>
                                    <input
                                        type="text"
                                        value={editingUser.countryPseudonym || ''}
                                        disabled
                                        style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                    />
                                </div>
                            </div>
                            <div className="modal-actions-custom">
                                <button type="button" className="btn-cancel" onClick={() => setEditingUser(null)}>Cancel</button>
                                <button type="submit" className="btn-save" disabled={isSaving}>
                                    {isSaving ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmModal 
                isOpen={confirmConfig.isOpen}
                onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                type={confirmConfig.type}
                singleButton={confirmConfig.singleButton}
                confirmText={confirmConfig.singleButton ? "Got it" : "Confirm"}
            />
        </div>
    );
};

export default UsersModule;